/**
 * Construction du contexte chronologique d'un individu pour l'extraction AI.
 * Regroupe les notes par individu et enrichit chaque groupe du profil complet.
 */

import { getName, getSurname, getBirthDate, getDeathDate, getBirthTownKey, getDeathTownKey, getDecompressedEvents } from '../gedcom/formatters/individualFormatters.js';
import familyTownsStore from '../tabs/familyMap/stores/familyTownsStore.js';
import gedcomDataStore from '../gedcom/stores/gedcomDataStore.js';
import { logger } from '../utils/debug/logger.js';

const MAX_KNOWN_EVENTS = 20;

/**
 * Construit le contexte chronologique d'un individu pour le prompt AI.
 * @param {string} individualId - ex: "@I123@"
 * @param {Map} individualsCache
 * @returns {Object|null} - Contexte structuré ou null si individu introuvable
 */
export function buildIndividualContext(individualId, individualsCache) {
    const individual = individualsCache.get(individualId);
    if (!individual) return null;

    const context = {
        id: individualId,
        name: getName(individual) || '',
        surname: getSurname(individual) || '',
    };

    // Naissance
    const birthDate = getBirthDate(individual);
    if (birthDate) context.birthDate = birthDate;
    const birthTownKey = getBirthTownKey(individual);
    if (birthTownKey) {
        const townData = familyTownsStore.getTownsData(birthTownKey);
        context.birthPlace = townData?.town || birthTownKey;
    }

    // Décès
    const deathDate = getDeathDate(individual);
    if (deathDate) context.deathDate = deathDate;
    const deathTownKey = getDeathTownKey(individual);
    if (deathTownKey) {
        const townData = familyTownsStore.getTownsData(deathTownKey);
        context.deathPlace = townData?.town || deathTownKey;
    }

    // Événements GEDCOM existants (exclure ceux déjà extraits par AI)
    const events = getDecompressedEvents(individual);
    const gedcomEvents = events.filter(e => !e.m || e.m.src !== 'ai');
    if (gedcomEvents.length > 0) {
        context.knownEvents = gedcomEvents
            .slice(0, MAX_KNOWN_EVENTS)
            .map(e => formatEventForContext(e));
    }

    // Conjoints avec dates de mariage
    const spouses = buildSpousesContext(individual, individualId, individualsCache);
    if (spouses.length > 0) {
        context.spouses = spouses;
    }

    return context;
}

/**
 * Formate un événement décompressé en chaîne lisible pour le contexte.
 * Ex: "occupation 1955 Fourmies Ingénieur (SNCF)"
 */
function formatEventForContext(event) {
    const parts = [];

    // Type
    parts.push(event.type || event.t || '?');

    // Date
    if (event.date) {
        parts.push(event.date);
    }

    // Lieu
    if (event.townKey || event.l) {
        const key = event.townKey || event.l;
        const townData = familyTownsStore.getTownsData(key);
        parts.push(townData?.town || key);
    }

    // Valeur (position, grade)
    if (event.m?.v) parts.push(event.m.v);

    // Organisation
    if (event.m?.org) parts.push(`(${event.m.org})`);

    // Summary pour les types sans position
    if (!event.m?.v && event.m?.sum) parts.push(event.m.sum);

    return parts.join(' ');
}

/**
 * Construit le contexte des conjoints d'un individu.
 */
function buildSpousesContext(individual, individualId, individualsCache) {
    const spouses = [];
    const familyIds = individual.fa || [];
    const { familiesCache } = gedcomDataStore;
    if (!familiesCache) return spouses;

    for (const familyId of familyIds) {
        const family = familiesCache.get(familyId);
        if (!family) continue;

        // Déterminer le conjoint
        let spouseId = null;
        if (family.h === individualId && family.w) spouseId = family.w;
        else if (family.w === individualId && family.h) spouseId = family.h;

        if (!spouseId) continue;
        const spouse = individualsCache.get(spouseId);
        if (!spouse) continue;

        const spouseInfo = {
            name: [getName(spouse), getSurname(spouse)].filter(Boolean).join(' '),
        };

        // Mariage depuis les événements familiaux
        if (family.e) {
            const marriageEvent = family.e.find(e => e.t === 'fm');
            if (marriageEvent) {
                if (marriageEvent.d) spouseInfo.marriageDate = String(marriageEvent.d);
                if (marriageEvent.l) {
                    const townData = familyTownsStore.getTownsData(marriageEvent.l);
                    spouseInfo.marriagePlace = townData?.town || marriageEvent.l;
                }
            }
        }

        spouses.push(spouseInfo);
    }

    return spouses;
}

/**
 * Regroupe toutes les notes éligibles par individu.
 * Chaque entrée contient le contexte chronologique + les notes de l'individu.
 *
 * @param {Map} notesCache - gedcomDataStore.notesCache
 * @param {Map} individualsCache - gedcomDataStore.individualsCache
 * @returns {Map<string, { context: Object, notes: Array<{id, text}> }>}
 */
export function groupNotesByIndividual(notesCache, individualsCache) {
    const grouped = new Map();

    for (const [noteId, noteData] of notesCache.entries()) {
        // Exclure les notes d'événements (déjà capturées comme événements structurés)
        if (noteId.startsWith('e_')) continue;
        // Exclure les notes de famille (source/acte, pas biographiques — économie de tokens)
        if (noteId.startsWith('f_')) continue;

        const text = noteData?.text || '';
        if (text.trim().length <= 10) continue;

        // Résoudre vers les individus liés
        const individualIds = resolveNoteToIndividuals(noteId, noteData, individualsCache);

        for (const individualId of individualIds) {
            if (!grouped.has(individualId)) {
                grouped.set(individualId, { notes: [] });
            }
            // Deduplicate: skip if an identical or near-identical text already exists
            const existing = grouped.get(individualId).notes;
            const trimmed = text.trim();
            const isDuplicate = existing.some(n => {
                const t = n.text.trim();
                return t === trimmed || t.includes(trimmed) || trimmed.includes(t);
            });
            if (!isDuplicate) {
                existing.push({ id: noteId, text });
            }
        }
    }

    // Construire le contexte pour chaque individu
    for (const [individualId, data] of grouped) {
        data.context = buildIndividualContext(individualId, individualsCache);
    }

    // Log supprimé : info dupliquée dans aiNotesIntegration.js
    return grouped;
}

/**
 * Résout un noteId vers les individus auxquels il est rattaché.
 * Reprend la logique de findIndividualsLinkedToNote (aiNotesIntegration.js).
 *
 * @param {string} noteId - ex: "i_@I123@_1", "f_@F456@_2"
 * @param {Object} noteData - données de la note depuis notesCache
 * @param {Map} individualsCache
 * @returns {string[]} - liste d'IDs individuels
 */
function resolveNoteToIndividuals(noteId, noteData, individualsCache) {
    // Notes individuelles : i_@I123@_N → extraire @I123@
    if (noteId.startsWith('i_')) {
        const match = noteId.match(/^i_(@I[^@]+@)/);
        if (match && individualsCache.has(match[1])) {
            return [match[1]];
        }
    }

    // Notes de famille : f_@F123@_N → résoudre via familiesCache → husband + wife
    if (noteId.startsWith('f_')) {
        const match = noteId.match(/^f_(@F[^@]+@)/);
        if (match) {
            const { familiesCache } = gedcomDataStore;
            const family = familiesCache?.get(match[1]);
            if (family) {
                const result = [];
                if (family.h && individualsCache.has(family.h)) result.push(family.h);
                if (family.w && individualsCache.has(family.w)) result.push(family.w);
                if (result.length > 0) return result;
            }
        }
    }

    // Fallback : champ individuals du noteData
    if (noteData?.individuals) {
        const result = [];
        for (const id of noteData.individuals) {
            if (individualsCache.has(id)) result.push(id);
        }
        if (result.length > 0) return result;
    }

    return [];
}
